import { imageDropped, iconDropped } from '../ui/stage-dnd';

module.exports = function (S) {
  const canvas = S.canvas;

  canvas.broadcastChannel.onmessage = (event) => {
    try {
      const payload = JSON.parse(event.data);

      if (payload.type !== 'canvasCopy') return;

      const serializedData = deserializeClipboard(payload.clipboard);

      if (payload.workspaceID === CONFIG.company_id) {
        canvas.clipBoard = serializedData;
        return;
      }

      // if not from the same workspace, remove custom-placeholder and data-defined-placeholder
      canvas.clipBoard = serializedData.map(data => {
        data.selectedElement.querySelectorAll('[custom-placeholder], [data-defined-placeholder]').forEach(node => {
          node.removeAttribute('custom-placeholder');
          node.removeAttribute('data-defined-placeholder');
        });

        return data;
      });
    } catch (error) {
      console.error('Error parsing the message', error);
    }
  };

  // We start by checking if the browser supports the
  // Clipboard object. If not, we need to create a
  // contenteditable element that catches all pasted data
  if (!window.Clipboard) {
    const pasteCatcher = document.createElement('div');

    // Firefox allows images to be pasted into contenteditable elements
    pasteCatcher.setAttribute('contenteditable', '');

    // We can hide the element and append it to the body,
    pasteCatcher.style.display = 'none';
    document.body.appendChild(pasteCatcher);

    // as long as we make sure it is always in focus
    pasteCatcher.focus();
    document.addEventListener('click', function () { pasteCatcher.focus(); });
  }

  document.addEventListener('copy', function (e) {
    // The is a bug in firefox that the e.target is not document activeElement
    const isFirefox = navigator.userAgent.toLowerCase().indexOf('firefox') > -1;
    const target = isFirefox && e.target !== document.activeElement
      ? document.activeElement
      : e.target;

    const invalidTarget =
      ['text', 'input', 'textarea'].includes(target.type) ||
      target.classList.contains('avoid-shortcuts');

    if (!invalidTarget) {
      e.clipboardData.clearData();
      copyHandler(e, target);
      e.preventDefault();
    }
  });

  document.addEventListener('paste', function (e) {
    const isFirefox = navigator.userAgent.toLowerCase().indexOf('firefox') > -1;
    const target = isFirefox && e.target !== document.activeElement
      ? document.activeElement
      : e.target;

    if (!target.matches('input, textarea, div[data-value="project-name"] span')) {
      pasteHandler(e);
    }
  });

  function copyHandler (event, target) {
    const canvas = INKIVE.Tool.getInstance().editor.canvas;

    if (canvas.getCurrentDrawing().getCurrentLayer().getAttribute('class') === 'clipperImage') return;

    canvas.copySelectedElements();

    // cross tabs copy & paste
    canvas.broadcastChannel.postMessage(JSON.stringify({
      type: 'canvasCopy',
      workspaceID: CONFIG.company_id,
      clipboard: serializeClipboard(canvas.clipBoard)
    }));

    // Temporary get data and remove it after
    const data = canvas.clipBoard;

    const selectedElementsString = [];
    const clipPathsstring = [];
    $.each(data, function (index, singleData) {
      const element = singleData.selectedElement;
      if (!element) return;
      if (element.nodeName.toLowerCase() === 'clippath') {
        clipPathsstring.push(INKIVE.Helpers.serializeToString(element));
      } else {
        selectedElementsString.push(INKIVE.Helpers.serializeToString(element));
      }
    });

    canvas.clipBoard = [];

    const svg = '<svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink">' +
      '<defs>' + clipPathsstring.join() + '</defs>' +
      '<g class="clipboard">' + selectedElementsString.join() + '</g></svg>';

    const textValue = $(target).val();

    if (textValue) {
      event.clipboardData.setData('text/plain', textValue);
    } else {
      event.clipboardData.setData('image/svg+xml', svg);
      const testSvg = event.clipboardData.getData('image/svg+xml');

      // In case if clipboard is not working
      if (svg !== testSvg || !canvas.clipBoard.length) {
        canvas.clipBoard = data;
      }
    }

    data[0] && event.clipboardData.setData('boundingBox', JSON.stringify(canvas.getBBox(data[0].selectedElement)));
  }

  function pasteHandler (e) {
    const editor = INKIVE.Tool.getInstance().editor;

    // We need to check if event.clipboardData is supported (Chrome)
    if (editor.canvas.clipBoard.length) {
      const contentElement = $('#svgroot #svgcontent').get(0);
      const selectedElement = editor.canvas.clipBoard[0].selectedElement;

      const clipBoardBoundingBox = e.clipboardData?.getData?.('boundingBox');
      const boundingBox = clipBoardBoundingBox
        ? JSON.parse(clipBoardBoundingBox)
        : editor.canvas.getBBox(selectedElement ?? contentElement);

      const isXOutside = boundingBox.x + boundingBox.width > editor.getOffset().width || boundingBox.x < 0;
      const isYOutside = boundingBox.y + boundingBox.height > editor.getOffset().height || boundingBox.y < 0;

      const point = {
        x: (isXOutside ? 0 : boundingBox.x) + boundingBox.width / 2,
        y: (isYOutside ? 0 : boundingBox.y) + boundingBox.height / 2
      };

      const pastedElements = editor.canvas.pasteElements(
        'point',
        point.x,
        point.y
      );
      editor.canvas.clipBoard = [];
      INKIVE.DesignFacade.trigger('paste', selectedElement?.id, pastedElements);

      return;
    }

    if (!e && !editor.canvas.clipBoard.length) {
      INKIVE.Modal.alertMessage(
        '<b>Your browser does not allow<br/>access to the clipboard</b><br/>Please use: CTRL + V'
      );

      return;
    }

    if (e?.clipboardData) {
      // Get the items from the clipboard
      const items = e.clipboardData.items;
      const canAddText = INKIVE.RestrictionsFacade.isFunctionalityAllowed('text_add');
      const canAddImage = INKIVE.RestrictionsFacade.isFunctionalityAllowed('image_add');

      if (!items) return;

      for (const item of items) {
        if (
          item.type === 'text/plain' &&
          !['textedit', 'table-edit'].includes(canvas.getMode()) &&
          canAddText
        ) {
          _createText({
            text: e.clipboardData.getData('text/plain')
          });
          INKIVE.DesignFacade.trigger('paste');

          continue;
        }

        if (item.type === 'image/svg+xml' && canAddImage) {
          iconDropped(item.getAsFile());

          continue;
        }

        if (['image/png', 'image/jpeg'].includes(item.type) && canAddImage) {
          imageDropped(item.getAsFile());
        }
      }
      // If we can't handle clipboard data directly (Firefox),
      // we need to read what was pasted from the contenteditable element

      return;
    }

    if (e) {
      // This is a cheap trick to make sure we read the data
      // AFTER it has been inserted.
      setTimeout(_checkInput, 1);
    }
  }

  function cutHandler (e) {
    const editor = INKIVE.Tool.getInstance().editor;
    editor.canvas.cutSelectedElements();
  }

  // ---- Private methods --------------------------------------------------------------------------------------------
  /**
   *  Parse the input in the paste catcher element
   **/
  function _checkInput () {
    const pasteCatcher = document.createElement('div');
    // Store the pasted content in a variable
    const child = pasteCatcher.childNodes[0];

    // Clear the inner html to make sure we're always
    // getting the latest inserted content
    pasteCatcher.innerHTML = '';

    if (child) {
      // If the user pastes an image, the src attribute
      // will represent the image as a base64 encoded string.
      if (child.tagName === 'IMG') {
        _createImage(child.src);
      }
    }
  }

  /**
   *  Creates a new image from a given source
   **/
  function _createImage (params) {
    const editor = INKIVE.Tool.getInstance().editor;
    const pastedImage = new Image();

    const offset = editor.getOffset();

    pastedImage.onload = function () {
      const imageX = offset.width / 2 - pastedImage.width / 2;
      const imageY = offset.height / 2 - pastedImage.height / 2;
      const imageWidth = pastedImage.width;
      const imageHeight = pastedImage.height;

      const image = editor.addElement('image', {
        url: params.dataURL,
        x: imageX,
        y: imageY,
        width: imageWidth,
        height: imageHeight
      });

      INKIVE.DesignFacade.trigger('paste');
      INKIVE.Tool.blinkScreen(image);
    };

    pastedImage.src = params.dataURL;
  }

  function _createText (params) {
    const editor = INKIVE.Tool.getInstance().editor;

    // Check for empty string
    if (params.text.length === 0) return;

    const font = {
      family: 'Lato',
      size: 12
    };

    const offset = editor.getOffset();

    fontSpy('Lato', {

      success: function () {
        let width;

        if (params.text.length > 400) {
          width = offset.width - 100;
        } else if (params.text.length > 200) {
          width = offset.width - 200;
          font.size = 20;
        } else {
          width = 200;
          font.size = 28;
        }
        const isRtl = /[\u0591-\u07FF]/.test(params.text);
        const elem = editor.addElement('textarea', {
          x: (offset.width - width) / 2,
          y: Math.floor4((offset.height - font.size) / 2),
          width,
          text: {
            font_family: font.family,
            font_size: font.size,
            textContent: params.text,
            direction: isRtl ? 'rtl' : null
          }
        });

        INKIVE.Tool.blinkScreen(elem);
      },

      failure: function () {
        INKIVE.Modal.alertMessage('Fail to init font', {
          skip: false
        });
      }
    });
  }

  /**
   *
   * @param {Clipboard} clipboard
   * @returns {{permission: string|null, content: string}[]}
   */
  function serializeClipboard (clipboard) {
    const serializer = new XMLSerializer();

    return clipboard.map((data) => {
      return {
        permission: data.permission,
        content: serializer.serializeToString(data.selectedElement)
      };
    });
  }

  /**
   *
   * @param {{permission: string|null, content: string}[]} serializedData
   * @returns {Clipboard}
   */
  function deserializeClipboard (serializedData) {
    const parser = new DOMParser();

    return serializedData.map(serializedElement => {
      const doc = parser.parseFromString(serializedElement.content, 'application/xml');
      return {
        permission: serializedElement.permission,
        selectedElement: doc.firstElementChild
      };
    });
  }

  return {

    copy: function () {
      copyHandler();
    },

    paste: function () {
      pasteHandler();
    },

    cut: function () {
      cutHandler();
    }

  };
};
